import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MaterialIcon from "../../../shared/components/MaterialIcon";
import { useTranslation } from "../../../i18n";
import {
  fetchAdminEmailTestPreview,
  fetchAdminNotificationConfig,
  sendAdminTestEmail,
  updateAdminNotificationConfig,
} from "../../../client/dashboard/notificationsApi";

function AlertToggle({ checked, onChange, label }) {
  return (
    <label className="relative inline-flex shrink-0 cursor-pointer items-center">
      <input
        type="checkbox"
        className="peer sr-only"
        checked={checked}
        onChange={onChange}
        aria-label={label}
      />
      <span
        className={[
          "relative h-8 w-14 rounded-full bg-outline-variant transition-colors",
          "after:absolute after:left-1 after:top-1 after:h-6 after:w-6 after:rounded-full",
          "after:border after:border-gray-300 after:bg-white after:transition-transform",
          "peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2",
          "peer-focus-visible:outline-secondary peer-checked:bg-secondary",
          "peer-checked:after:translate-x-6",
        ].join(" ")}
        aria-hidden
      />
    </label>
  );
}

const AdminEmailAlertsPage = () => {
  const { t } = useTranslation();
  const [config, setConfig] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [transport, setTransport] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [cfg, testMeta] = await Promise.all([
          fetchAdminNotificationConfig(),
          fetchAdminEmailTestPreview(),
        ]);
        if (!active) return;
        setConfig(cfg);
        setTransport(testMeta.transport || null);
        setPreview(testMeta.preview || null);
      } catch {
        if (active) setError(t("emailAlerts.loadError"));
      }
    })();
    return () => {
      active = false;
    };
  }, [t]);

  const patch = useCallback((changes) => {
    setConfig((prev) => (prev ? { ...prev, ...changes } : prev));
  }, []);

  const showToast = (message) => {
    setToastMessage(message);
    setToastVisible(true);
    window.setTimeout(() => setToastVisible(false), 3000);
  };

  const handleSave = async () => {
    if (!config || saving) return;
    setSaving(true);
    setError("");
    try {
      const saved = await updateAdminNotificationConfig(config);
      setConfig(saved);
      showToast(t("common.preferencesSaved"));
    } catch {
      setError(t("emailAlerts.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    if (testing) return;
    setTesting(true);
    setError("");
    try {
      const result = await sendAdminTestEmail();
      if (result.transport) setTransport(result.transport);
      showToast(
        t("adminEmailAlertsReal.testSent", {
          email: result.to_email || preview?.to_email || "",
        })
      );
    } catch {
      setError(t("adminEmailAlertsReal.testError"));
    } finally {
      setTesting(false);
    }
  };

  const cards = config
    ? [
        {
          key: "notifyOnNewCase",
          icon: "gavel",
          iconBoxClass: "bg-primary-container",
          iconClass: "text-primary-fixed",
          title: t("adminEmailAlertsReal.newCase"),
          hint: t("adminEmailAlertsReal.newCaseHint"),
          checked: config.notifyOnNewCase,
        },
        {
          key: "notifyOnNewTicket",
          icon: "support_agent",
          iconBoxClass: "bg-secondary-container",
          iconClass: "text-on-secondary-container",
          title: t("adminEmailAlertsReal.newTicket"),
          hint: t("adminEmailAlertsReal.newTicketHint"),
          checked: config.notifyOnNewTicket,
        },
      ]
    : [];

  return (
    <main className="min-h-screen bg-background font-body-md text-on-surface antialiased">
      <div className="mx-auto max-w-container-max px-margin-mobile py-12 md:px-margin-desktop">
        <nav className="mb-stack-lg">
          <Link
            to="/dashboard/admin"
            className="group inline-flex items-center gap-2 transition-colors"
          >
            <MaterialIcon
              name="arrow_back"
              className="text-[20px] text-outline transition-colors group-hover:text-primary"
            />
            <span className="font-label-sm text-label-sm text-outline transition-colors group-hover:text-primary">
              {t("common.backToDashboard")}
            </span>
          </Link>
        </nav>

        <header className="mb-12">
          <h1 className="mb-stack-sm font-headline-xl text-headline-xl text-on-background">
            {t("adminEmailAlerts.title")}
          </h1>
          <p className="max-w-2xl font-body-lg text-body-lg text-on-surface-variant">
            {t("adminEmailAlerts.subtitle")}
          </p>
        </header>

        {error ? (
          <p className="mb-6 rounded-lg bg-error-container px-4 py-3 text-label-sm text-on-error-container">
            {error}
          </p>
        ) : null}

        {transport ? (
          <section className="mb-8 rounded-xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-soft md:p-8">
            <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
              <div className="min-w-0">
                <h2 className="mb-2 font-headline-md text-headline-md text-primary">
                  {t("adminEmailAlertsReal.deliveryTitle")}
                </h2>
                <p className="mb-3 font-body-md text-body-md text-on-surface-variant">
                  {t("adminEmailAlertsReal.deliveryTransport", {
                    transport: transport.label,
                  })}
                </p>
                <p className="font-body-md text-body-md text-on-surface-variant">
                  {t("adminEmailAlertsReal.deliveryRecipient", {
                    email: preview?.to_email || "—",
                  })}
                </p>
                {!transport.production_ready ? (
                  <p className="mt-3 font-label-sm text-label-sm text-outline">
                    {t("adminEmailAlertsReal.deliveryDevHint")}
                  </p>
                ) : null}
              </div>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => setPreviewOpen(true)}
                  disabled={!preview?.html}
                  className="inline-flex items-center gap-2 rounded-lg border border-outline-variant px-5 py-3 font-label-sm text-label-sm text-on-surface transition-colors hover:bg-surface-container disabled:opacity-50"
                >
                  <MaterialIcon name="visibility" className="text-[18px]" />
                  {t("adminEmailAlertsReal.previewButton")}
                </button>
                <button
                  type="button"
                  onClick={handleSendTest}
                  disabled={testing}
                  className="inline-flex items-center gap-2 rounded-lg bg-secondary px-5 py-3 font-label-sm text-label-sm text-on-secondary transition-all hover:-translate-y-0.5 disabled:opacity-60"
                >
                  <MaterialIcon name="send" className="text-[18px]" />
                  {testing
                    ? t("adminEmailAlertsReal.sendingTest")
                    : t("adminEmailAlertsReal.testButton")}
                </button>
              </div>
            </div>
          </section>
        ) : null}

        <div className="space-y-gutter">
          {cards.map((card) => (
            <article
              key={card.key}
              className="flex items-center justify-between gap-6 rounded-xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-soft transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(15,23,42,0.1)] md:p-8"
            >
              <div className="flex min-w-0 items-start gap-6">
                <div
                  className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg ${card.iconBoxClass}`}
                >
                  <MaterialIcon
                    name={card.icon}
                    className={`text-[22px] ${card.iconClass}`}
                  />
                </div>
                <div className="min-w-0">
                  <h3 className="mb-1 font-headline-md text-headline-md text-primary">
                    {card.title}
                  </h3>
                  <p className="font-body-md text-body-md text-on-surface-variant">
                    {card.hint}
                  </p>
                </div>
              </div>
              <AlertToggle
                checked={card.checked}
                onChange={() => patch({ [card.key]: !card.checked })}
                label={card.title}
              />
            </article>
          ))}
        </div>

        {config ? (
          <footer className="mt-12 flex justify-start">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="group flex items-center gap-3 rounded-lg bg-primary-container px-10 py-4 font-headline-md text-white shadow-soft transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(15,23,42,0.1)] active:scale-95 disabled:opacity-60"
            >
              {t("emailAlerts.savePreferences")}
              <MaterialIcon
                name="arrow_forward"
                className="text-[20px] transition-transform group-hover:translate-x-1"
              />
            </button>
          </footer>
        ) : null}
      </div>

      {previewOpen && preview?.html ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-label={t("adminEmailAlertsReal.previewTitle")}
          onClick={() => setPreviewOpen(false)}
        >
          <div
            className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-surface-container-lowest shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-outline-variant/30 px-6 py-4">
              <div>
                <h2 className="font-headline-md text-headline-md text-primary">
                  {t("adminEmailAlertsReal.previewTitle")}
                </h2>
                <p className="mt-1 font-label-sm text-label-sm text-outline">
                  {preview.subject}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPreviewOpen(false)}
                className="rounded-lg p-2 text-outline transition-colors hover:bg-surface-container hover:text-on-surface"
                aria-label={t("common.close")}
              >
                <MaterialIcon name="close" className="text-[22px]" />
              </button>
            </div>
            <div className="overflow-auto bg-[#f4f6f7] p-4">
              <iframe
                title={t("adminEmailAlertsReal.previewTitle")}
                srcDoc={preview.html}
                className="h-[60vh] w-full rounded-lg border-0 bg-white"
                sandbox=""
              />
            </div>
          </div>
        </div>
      ) : null}

      <div
        role="status"
        aria-live="polite"
        className={[
          "pointer-events-none fixed bottom-10 right-10 z-50 flex items-center gap-3 rounded-xl bg-on-secondary-container px-6 py-4 text-white shadow-xl transition-all duration-300",
          toastVisible ? "translate-y-0 opacity-100" : "translate-y-20 opacity-0",
        ].join(" ")}
      >
        <MaterialIcon name="check_circle" className="text-[22px]" />
        <span className="font-body-md">{toastMessage}</span>
      </div>
    </main>
  );
};

export default AdminEmailAlertsPage;
